import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DiscrepancySection = ({ discrepancies, onResolve, onAddAdjustment }) => {
  const getDiscrepancyIcon = (type) => {
    switch (type) {
      case 'bank_fee': return 'DollarSign';
      case 'interest': return 'TrendingUp';
      case 'timing': return 'Clock';
      case 'error': return 'AlertTriangle';
      default: return 'AlertCircle';
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border border-border">
      <div className="p-4 md:p-6 border-b border-border">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-error/10 rounded-md flex items-center justify-center">
              <Icon name="AlertTriangle" size={20} color="var(--color-error)" />
            </div>
            <div>
              <h3 className="font-heading font-semibold text-base md:text-lg">Discrepancies</h3>
              <p className="caption text-muted-foreground">
                {discrepancies?.length} items require attention
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onAddAdjustment}
            iconName="Plus"
            iconPosition="left"
          >
            Add Adjustment
          </Button>
        </div>
      </div>
      <div className="p-4 md:p-6 space-y-3 max-h-96 overflow-y-auto">
        {discrepancies?.map((discrepancy) => (
          <div
            key={discrepancy?.id}
            className="p-4 rounded-lg border border-error/20 bg-error/5 transition-smooth hover:shadow-md"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <Icon name={getDiscrepancyIcon(discrepancy?.type)} size={16} color="var(--color-error)" />
                  <span className="caption font-medium text-error">
                    {discrepancy?.type?.replace('_', ' ')?.toUpperCase()}
                  </span>
                </div>
                <p className="font-body text-sm md:text-base text-foreground line-clamp-2">
                  {discrepancy?.description}
                </p>
                <p className="caption text-muted-foreground mt-1">
                  {discrepancy?.date} • {discrepancy?.reference}
                </p>
              </div>
              <span className="data-text text-lg md:text-xl font-semibold text-error whitespace-nowrap">
                ${Math.abs(discrepancy?.amount)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>

            {discrepancy?.suggestedAction && (
              <div className="mb-3 p-3 rounded-md bg-warning/10 border border-warning/20">
                <div className="flex items-start gap-2">
                  <Icon name="Lightbulb" size={16} color="var(--color-warning)" className="flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="caption font-medium text-warning mb-1">Suggested Action</p>
                    <p className="caption text-foreground">{discrepancy?.suggestedAction}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onResolve(discrepancy?.id, 'accept')}
                iconName="Check"
                iconPosition="left"
                fullWidth
              >
                Accept
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onResolve(discrepancy?.id, 'investigate')}
                iconName="Search"
                iconPosition="left"
                fullWidth
              >
                Investigate
              </Button>
            </div>
          </div>
        ))}

        {discrepancies?.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircle2" size={32} color="var(--color-success)" />
            </div>
            <p className="font-body text-success">No discrepancies found</p>
            <p className="caption text-muted-foreground mt-2">
              All transactions are properly matched
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DiscrepancySection;